/**
 * Calculates all totals for events. Included in the Event
 * service and includes the service for event item totals
 */
export function EventTotalsService (bigDecimalApi, sessionObjAPI, EventItemTotals) {

  var EventTotals = function (event) {
    this.company     = sessionObjAPI.company();
    this.event       = event;
    this.hasGroupTax = false;
    this.info        = sessionObjAPI.currency();
    this.itemTotals  = new EventItemTotals(event);
  };

  var calculateMultiTax = function (taxRates, total, totalExcludingLabor, skipDisplay) {
    var tax = BigDecimal.ZERO;

    // If the value is less than 0, a discount is being applied
    // that is greater than the amount owed. Tax is zero.
    total = total < 0 ? BigDecimal.ZERO: total;
    totalExcludingLabor = totalExcludingLabor < 0 ? BigDecimal.ZERO: totalExcludingLabor;

    angular.forEach(taxRates, function (rate) {

      // If the rate has labor_gst_free, we want to calculate
      // tax based off the total excluding labor.
      rate.multi_tax_result = rate.labor_gst_free ?
        totalExcludingLabor.multiply(rate.tax_rate.movePointLeft(2)).setScale(2, BigDecimal.ROUND_HALF_UP) :
        total.multiply(rate.tax_rate.movePointLeft(2)).setScale(2, BigDecimal.ROUND_HALF_UP);

      rate.multi_tax_result_display = rate.multi_tax_result.setScale(4, BigDecimal.ROUND_HALF_UP).toString();
    });

    angular.forEach(taxRates, function (rate) {
      tax = tax.add(rate.multi_tax_result);
    });

    return tax;
  };

  var calculateSalesTax = function (taxRate, total) {
    return total.multiply(taxRate);
  };

  var getTaxRates = function (taxGroup) {
    var taxRates = taxGroup.tax_group_items_attributes;

    angular.forEach(taxRates, function (rate) {
      rate.tax_rate = new BigDecimal(rate.tax_rate ? rate.tax_rate.toString(): '0').setScale(2, BigDecimal.ROUND_HALF_UP);
    });

    return taxRates;
  };

  /**
   * Determines whether or not the event is closed based on status
   * @return {boolean} true if closed, false if open
   */
  EventTotals.prototype.closed = function () {
    var self = this;

    return self.event.event_status === 'C';
  };

  /**
   * Determines if the specified value is equal to zero.
   * Sometimes values are stores as the string "0.00".
   * In JavaScript, this will not show as 0, so we need
   * parse and check.
   * @param  {variable} value The value to check
   * @return {boolean}       true if zero, false if not
   */
  EventTotals.prototype.isZero = function(value) {
    var self = this;

    value = new BigDecimal(value ? value.toString(): '0');

    return value.isZero();
  };

  EventTotals.prototype.calculateTaxByItems = function ( skips = {} ) {
    const self = this;

    let tax = bigDecimalApi.get();

    if ( !skips.freight ) {
      const freight = self.company.tax_freight
        ? bigDecimalApi.get(self.event.freight)
        : BigDecimal.ZERO;

      const taxRate = bigDecimalApi.get(self.company.sales_tax_rate).movePointLeft(2);

      const freightTax = self.info.price_includes_tax
        ? freight.multiply(taxRate)
          .divide(BigDecimal.ONE.add(taxRate))
        : freight.multiply(taxRate);

      tax = tax.add(freightTax.setScale(2, BigDecimal.prototype.ROUND_HALF_UP));
    }

    angular.forEach(self.event.event_items_attributes, ( item ) => {
      tax = tax.add(bigDecimalApi.get(self.itemTotals.tax(item)));
    });

    return tax;
  };

  /**
   * If the flag "calculate_tax_on_subtotal" is set, then tax needs to be calculated
   * at the subtotal level, not just the total of all the item's tax calclulations.
   * This handles that form of calculations
   * @return {object} The Big Decimal value for the calculated tax
   */
  EventTotals.prototype.calculateTaxBySubtotal = function (skips) {
    var self = this;
    var skips = skips || {};
    var subtotal = BigDecimal.ZERO;
    var subtotalExcludingLabor = BigDecimal.ZERO;
    var discount = new BigDecimal(self.event.discount ? self.event.discount.toString(): '0.00');
    var freight = self.company.tax_freight ? new BigDecimal(self.event.freight ? self.event.freight.toString(): '0'): BigDecimal.ZERO;
    var tax;
    var taxRate = new BigDecimal(self.company.sales_tax_rate).movePointLeft(2);
    var taxRates, totalDiscount, subtotalDiscount;

    if (self.company.use_multi_tax) {
      taxRates = getTaxRates(self.event.tax_group);
    }

    // loop through the items and get the total of them, without taxes
    angular.forEach(self.event.event_items_attributes, function (item) {
      var quantity = BigDecimal.ZERO;
      var unit_price = BigDecimal.ZERO;

      // We only need the items where tax is included
      if (!item.gst_free) {
        unit_price = new BigDecimal( item.unit_price ? item.unit_price.toString(): '0' ).setScale(2, BigDecimal.ROUND_HALF_UP);
        quantity = new BigDecimal( item.quantity && item.quantity !== '-' ? item.quantity.toString(): '0' ).setScale(2, BigDecimal.ROUND_HALF_UP);
        subtotal = subtotal.add(unit_price.multiply(quantity).setScale(2, BigDecimal.ROUND_HALF_UP));

        // Need to keep track of the subtotal excluding labor
        // items for use in tax groups who have labor_gst_free
        // rates.
        subtotalExcludingLabor = item.product_type === 'J' ?
          subtotalExcludingLabor:
          subtotalExcludingLabor.add(unit_price.multiply(quantity).setScale(2, BigDecimal.ROUND_HALF_UP));
      }

    });

    // Dont forget the freight amount
    if (!skips.freight) {
      subtotal               = subtotal.add(freight);
      subtotalExcludingLabor = subtotalExcludingLabor.add(freight);
    }

    // Now let's calculate the tax based on the subtotal of items

    // If the price includes tax, extract the tax from the total
    if (self.info.price_includes_tax) {
      tax = subtotal.subtract(
        subtotal.setScale(4, BigDecimal.ROUND_HALF_UP)
        .divide(
          BigDecimal.ONE.add(taxRate)
          .setScale(4, BigDecimal.ROUND_HALF_UP)
        )
      );

      tax = tax.setScale(2, BigDecimal.ROUND_HALF_UP);
    }

    // If not, calculate the tax based on the tax rate
    else {

      //if there is a discount, apply it;
      if (!discount.isZero() && !skips.discount) {
        totalDiscount          = new BigDecimal(self.totalDiscount());
        subtotalDiscount       = new BigDecimal(self.subtotalDiscount(totalDiscount.toString()));
        subtotal               = subtotal.subtract(subtotalDiscount);
        subtotalExcludingLabor = subtotalExcludingLabor.subtract(subtotalDiscount);
      }

      // Calculate tax based on use_multi_tax
      tax = self.company.use_multi_tax ?
        calculateMultiTax(taxRates, subtotal, subtotalExcludingLabor, (skips.discount && skips.freight)):
        calculateSalesTax(taxRate, subtotal);
    }

    return tax;
  };

  /**
   * Calculates the balance due for the whole event
   * @return {string} the balance due for the event
   */
  EventTotals.prototype.balance = function () {
    var self = this;
    var total = new BigDecimal(self.total().toString()).setScale(2, BigDecimal.ROUND_HALF_UP);

    // If the event is close, return what is sent from the server
    if (self.closed()) {
      return self.event.balance_due || '0';
    }

    // If not, return the total
    return total.toString();
  };

  /**
   * Calculates the tax for the whole event
   * @return {string} The total tax for the event
   */
  EventTotals.prototype.tax = function (skips) {
    var self = this;
    var skips = skips || {};
    var calculatedTaxRate = BigDecimal.ZERO;
    var tax = BigDecimal.ZERO;
    var freight = self.company.tax_freight ? new BigDecimal(self.event.freight ? self.event.freight.toString(): '0'): BigDecimal.ZERO;
    var taxRate = new BigDecimal(self.company.sales_tax_rate).movePointLeft(2);
    var discount = new BigDecimal(self.event.discount ? self.event.discount.toString(): '0.00');
    var taxRates, totalDiscount, taxDiscount;

    if (self.company.use_multi_tax) {
      taxRates = getTaxRates(self.event.tax_group);
    }

    // If the event is closed, return the amount from the server
    if (self.closed()) {
      return self.event.gst;
    }

    // If the customer is tax free, return 0
    else if (self.event.tax_free) {
      return tax.toString();
    }

    else if (!self.info.price_includes_tax && self.company.has_xero) {
      tax = self.calculateTaxByItems(skips);

      // The next if block is for handling discounts with Xero
      // users. First we need to get our calculatedTaxRate,
      // described below. Then subtract from the tax calculated
      // above the applied discount.

      // set the calculated tax rate. For non multi-tax users, this
      // is just the normal taxRate. For multi-tax users, we add up
      // each rate in the tax group. This will be used to calculate
      // the discount from tax, if applicable.
      if (self.company.use_multi_tax) {
        angular.forEach(taxRates, function (rate) {
          calculatedTaxRate = calculatedTaxRate.add(rate.tax_rate.movePointLeft(2));
        });
      } else {
        calculatedTaxRate = calculatedTaxRate.add(taxRate);
      }

      // apply discount to the xero-specific calculated tax.
      if ( !discount.isZero() ) {

        // discount is cash
        if (self.event.discount_type === 'C') {
          var taxDiscount = new BigDecimal(self.taxDiscount(discount.toString()))
          tax = tax.subtract(taxDiscount.setScale(2, BigDecimal.ROUND_HALF_UP));
        }

        // discount is a percentage
        else {
          if (!skips.discount) {
            tax = tax.subtract(tax.multiply(discount.movePointLeft(2)).setScale(2, BigDecimal.ROUND_HALF_UP));
          }
        }

        //make sure tax isn't negative
        tax = tax < 0 ? BigDecimal.ZERO: tax;
      }

      // The only reason for calling this is
      // to set each tax rate's multi_tax_result_display
      // property. We don't do anything with
      // the returned tax value because taxes are
      // calculated differently for Xero users.
      self.calculateTaxBySubtotal(skips);
    }

    else {
      tax = self.hasGroupTax
      ? self.calculateTaxByItems(skips)
      : self.calculateTaxBySubtotal(skips);
    }

    // If there is no discount or if the discount is applied
    // to the subtotal (pretax, based on price_includes_tax),
    // just return what we have so far.
    if (discount.isZero() || skips.discount || !self.info.price_includes_tax) {
      return tax.setScale(2, BigDecimal.ROUND_HALF_UP).toString();
    }

    // Unsure if this needs any changing for multi tax. The discount
    // should be applied to the total, meaning price_includes_tax is
    // true. That would  mean use_multi_tax is false.
    totalDiscount = new BigDecimal(self.totalDiscount());
    taxDiscount = new BigDecimal(self.taxDiscount(totalDiscount.toString()));

    return tax.subtract(taxDiscount).setScale(2, BigDecimal.ROUND_HALF_UP).toString();
  };

  /**
   * Calculates the subtotal for the invoice
   * when price_includes_tax is on
   * accounts for freight being considered a line item
   * so this amount is different than subtotal (which excludes freight,
   * necessary for earlier calculations such as on discounts)
   * @return {string} The subtotal amount
   */
  EventTotals.prototype.includesTaxSubtotal = function() {
    const self = this;

    let taxableTotal = BigDecimal.ZERO;
    let total        = BigDecimal.ZERO;

    const freight = bigDecimalApi.get(self.event.freight, 2);

    // Calculate totals from line items
    angular.forEach(self.event.event_items_attributes, item => {
      const itemTotal = bigDecimalApi.get(self.itemTotals.total(item));

      total = total.add(itemTotal);

      if (!item.gst_free && ! self.event.tax_free) {
        taxableTotal = taxableTotal.add(itemTotal);
      }
    });

    // Calculate initial untaxable total from line items
    let untaxableTotal = total.subtract(taxableTotal);

    /**
     * Handle freight based on whether it should be taxed
     * or not.
     *
     * If tax_freight is true then add it to the taxable
     * total.
     *
     * If it is false, then add it to the untaxable total.
     */
    if (self.company.tax_freight) {
      taxableTotal = taxableTotal.add(freight);
    }
    else {
      untaxableTotal = untaxableTotal.add(freight);
    }

    const taxableSubtotal = taxableTotal.subtract(self.calculateTaxByItems());
    const subtotal        = taxableSubtotal.add(untaxableTotal).subtract(freight);

    return subtotal
      ? subtotal.toString()
      : '0';
  };

  /**
   * Calculates the subtotal for the event
   * @return {string} The subtotal amount
   */
  EventTotals.prototype.subtotal = function(skips) {
    var self = this;
    var subtotal = BigDecimal.ZERO;
    var total = BigDecimal.ZERO
    var tax = BigDecimal.ZERO;
    var taxableTotal = BigDecimal.ZERO;
    var discount = new BigDecimal(self.event.discount ? self.event.discount.toString(): '0.00');
    var taxRate = new BigDecimal(self.company.sales_tax_rate).movePointLeft(2);
    var totalDiscount, subtotalDiscount;

    skips = skips || {};


    // If the event is closed, no need to do any calculations
    if (self.closed()) {
      return self.event.subtotal || '0';
    }

    // if the calculate tax flag is set, we need to get the tax out on the invoice level
    else if (self.info.price_includes_tax && !self.event.tax_free) {
      angular.forEach(self.event.event_items_attributes, function (item) {
        var itemTotal = new BigDecimal( self.itemTotals.total(item) );

        total = total.add(itemTotal);
      });

      subtotal = total.subtract(self.calculateTaxByItems({ freight: true }));
    }

    // If not, just add up each items total before taxes
    else {
      angular.forEach(self.event.event_items_attributes, function (item){
        var unit_price = new BigDecimal( item.unit_price ? item.unit_price.toString(): '0' );
        var quantity = new BigDecimal( item.quantity && item.quantity !== '-' ? item.quantity.toString(): '0' );
        subtotal = subtotal.add( unit_price.multiply(quantity).setScale(2, BigDecimal.ROUND_HALF_UP) );
      });
    }

    // If there is a discount, apply it.
    if (!discount.isZero() && !self.info.price_includes_tax && !skips.discount) {
      totalDiscount    = new BigDecimal(self.totalDiscount());
      subtotalDiscount = new BigDecimal(self.subtotalDiscount(totalDiscount.toString()));
      subtotal         = subtotal.subtract(subtotalDiscount);
    }

    return subtotal ? subtotal.toString(): '0';
  };

  /**
   * Calculate the total of the event
   * @return {string} The total for the event
   */
  EventTotals.prototype.total = function() {
    const self = this;

    const discount = bigDecimalApi.get(self.event.discount);

    const tax = self.event.tax_free
      ? BigDecimal.ZERO
      : bigDecimalApi.get(self.tax({ discount: self.hasGroupTax }), 2);

    let subtotal = self.info.price_includes_tax
      ? bigDecimalApi.get(self.includesTaxSubtotal())
      : bigDecimalApi.get(self.subtotal());

    let total = BigDecimal.ZERO;
    let totalDiscount, subtotalDiscount;

    if (self.closed()) {
      return self.event.total || '0';
    }

    // if there is a discount, apply it;
    if (!discount.isZero() && self.info.price_includes_tax) {
      totalDiscount    = bigDecimalApi.get(self.totalDiscount());
      subtotalDiscount = bigDecimalApi.get(self.subtotalDiscount(totalDiscount.toString()));
      subtotal         = subtotal.subtract(subtotalDiscount);
    }

    const freight = bigDecimalApi.get(self.event.freight);

    /**
     * Add tax and freight to the subtotal.
     */
    total = subtotal.add(tax).add(freight);

    // Round the total if the flag is set
    if (self.info.round_total) {
      return bigDecimalApi.round(total.toString());
    }

    return total.toString();
  };

  EventTotals.prototype.rounding = function() {
    var self = this;
    var total, tax, freight, roundedTotal;
    var subtotal = self.info.price_includes_tax ? new BigDecimal(self.includesTaxSubtotal()) : new BigDecimal(self.subtotal());

    if (!self.info.round_total) {
      return '0';
    }
    else if (self.closed()) {
      return self.event.rounding;
    }
    else {
      tax = self.event.tax_free ? BigDecimal.ZERO: new BigDecimal(self.tax({ discount: self.hasGroupTax })).setScale(2, BigDecimal.ROUND_HALF_UP);
      freight = new BigDecimal( self.event.freight ? self.event.freight.toString(): '0' ).setScale(2, BigDecimal.ROUND_HALF_UP);
      roundedTotal = new BigDecimal(bigDecimalApi.round(subtotal.add(tax).add(freight).toString()));

      return (subtotal.add(tax).add(freight)).subtract(roundedTotal).negate().toString();
    }
  };

  EventTotals.prototype.totalDiscount = function() {
    var self = this;
    var subtotal, tax, total;
    var discount = new BigDecimal(self.event.discount ? self.event.discount.toString(): '0').setScale(2, BigDecimal.ROUND_HALF_UP);

    if (discount.isZero() || self.event.discount_type === 'C') {
      return discount ? discount.toString(): '0.00';
    }

    // If not it is a percentage so move the decimal
    discount = discount.movePointLeft(2);

    subtotal = new BigDecimal(self.subtotal({discount: true}));
    tax = new BigDecimal(self.tax({discount: true, freight: true}));
    total = subtotal.add(tax);

    return self.info.discount_includes_tax ?
      total.multiply(discount).setScale(2, BigDecimal.ROUND_HALF_UP).toString():
      subtotal.multiply(discount).setScale(2, BigDecimal.ROUND_HALF_UP).toString();
  };

  EventTotals.prototype.taxDiscount = function(totalDiscount) {
    var self = this;
    var taxRate = new BigDecimal(self.company.sales_tax_rate).movePointLeft(2);

    totalDiscount = new BigDecimal(totalDiscount || '0.00').setScale(4, BigDecimal.ROUND_HALF_UP);

    return self.info.discount_includes_tax ? totalDiscount.subtract(totalDiscount.divide(BigDecimal.ONE.add(taxRate))).toString() : totalDiscount.multiply(taxRate).toString() ;//totalDiscount.subtract(totalDiscount.divide(BigDecimal.ONE.add(taxRate))).toString();
  };

  EventTotals.prototype.subtotalDiscount = function(totalDiscount) {
    var self = this;
    var taxDiscount;

    // If the total was not passed then we need to calculate it
    totalDiscount = new BigDecimal(totalDiscount || self.totalDiscount());

    taxDiscount = new BigDecimal(self.taxDiscount(totalDiscount.toString()));

    // If discount is applied to subtotal (price_includes_tax is false),
    // return the total discount.
    return self.info.discount_includes_tax ?
      totalDiscount.subtract(taxDiscount).setScale(2, BigDecimal.ROUND_HALF_UP).toString():
      totalDiscount.toString();
  };

  return EventTotals;
}